import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CircleNotch, X, Lightbulb, ClockCounterClockwise } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle, focusFirstIn } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { readDraft, writeDraft, clearDraft } from "../../lib/recent";
import { relativeTime } from "../../lib/theme";
import GreenhouseArt from "./GreenhouseArt";
import AmbulanceArt from "./AmbulanceArt";
import { BAND } from "./MiniScene";

export const SHEET = "p-0 gap-0 overflow-hidden flex flex-col max-h-[90dvh] max-sm:max-h-none max-sm:h-[94dvh] max-sm:w-full max-sm:max-w-none max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-[28px] max-sm:data-[state=open]:slide-in-from-bottom-full max-sm:data-[state=closed]:slide-out-to-bottom-full max-sm:data-[state=open]:zoom-in-100";
const spring = { type: "spring", stiffness: 320, damping: 26 };

export function Progress({ steps, step }) {
  return (
    <div className="flex gap-1.5" data-testid="wizard-progress" aria-label={`Step ${step + 1} of ${steps.length}`}>
      {steps.map((s, i) => (
        <span key={s.key} className="h-1 flex-1 rounded-full bg-muted overflow-hidden">
          <span className="block h-full rounded-full bg-foreground transition-[width] duration-500 ease-out" style={{ width: i < step ? "100%" : i === step ? "50%" : "0%" }} />
        </span>
      ))}
    </div>
  );
}

function DoneScreen({ done, color = "#34C759", onClose }) {
  return (
    <div className={`relative isolate flex-1 flex flex-col items-center text-center px-8 ${done.art ? "pb-10" : "justify-center py-12"}`} style={{ "--band": BAND, containerType: "inline-size" }} data-testid="wizard-done">
      {done.art === "greenhouse" ? <GreenhouseArt color={color} /> : done.art === "ambulance" ? <AmbulanceArt /> : null}
      {done.art ? <div aria-hidden className="shrink-0" style={{ height: "calc(var(--band) - 18px)" }} /> : (
      <div className="relative h-24 w-24">
        {[0, 1].map((k) => (
          <motion.span key={k} className="absolute inset-0 rounded-full" style={{ border: `2px solid ${color}` }} initial={{ scale: 0.6, opacity: 0.7 }} animate={{ scale: 1.9, opacity: 0 }} transition={{ duration: 1.3, delay: 0.15 + k * 0.35, ease: "easeOut" }} />
        ))}
        <motion.span className="absolute inset-0 rounded-full inline-flex items-center justify-center text-white shadow-[0_16px_40px_-12px_rgba(0,0,0,0.35)]" style={{ background: color }}
          initial={{ scale: 0.3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={spring}>
          <svg viewBox="0 0 24 24" className="h-11 w-11" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <motion.path d="M5 12.5l4.5 4.5L19 7.5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
          </svg>
        </motion.span>
      </div>
      )}
      <motion.div className="relative" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: done.art === "ambulance" ? 3.8 : done.art ? 1.2 : 0.25 }}>
        <DialogTitle className={`${done.art ? "mt-0" : "mt-8"} text-2xl font-semibold tracking-tight`} data-testid="wizard-done-title">{done.title}</DialogTitle>
        {done.sub && <DialogDescription className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">{done.sub}</DialogDescription>}
      </motion.div>
      <motion.div className="relative mt-8 w-full max-w-xs flex flex-col gap-2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: done.art === "ambulance" ? 3.9 : done.art ? 1.3 : 0.35 }}>
        {(done.actions || []).map((a, i) => (
          <button key={a.label} type="button" onClick={a.onClick} data-testid={`wizard-done-action-${i}`}
            className={`h-11 rounded-full text-sm font-semibold active:scale-[0.97] transition-transform ${i === 0 ? "bg-foreground text-background" : "bg-muted/70 hover:bg-muted"}`}>{a.label}</button>
        ))}
        <button type="button" onClick={onClose} data-testid="wizard-done-close" className="h-11 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/40">Done</button>
      </motion.div>
    </div>
  );
}

export default function WizardShell({
  open, onOpenChange, testid, kicker, steps, step, onBack, onNext, canNext = true, isLast, onFinish, finishLabel, finishStyle, saving, maxW = "max-w-2xl", children, hideFooter, dirty = false, discardText = "What you've entered so far will be lost.",
  draftKey, draftValue, onRestoreDraft, done, doneColor, onSkip,
}) {
  const s = steps[step];
  const [confirm, setConfirm] = useState(false);
  const [saved, setSaved] = useState(null);
  const restored = useRef(false);
  const json = draftKey ? JSON.stringify(draftValue) : "";

  useEffect(() => {
    if (!open || !draftKey) return;
    restored.current = false;
    setSaved(readDraft(draftKey));
  }, [open, draftKey]);
  useEffect(() => {
    if (!open || !draftKey || !dirty || done) return undefined;
    const t = setTimeout(() => writeDraft(draftKey, JSON.parse(json)), 400);
    return () => clearTimeout(t);
  }, [open, draftKey, json, dirty, done]);
  useEffect(() => { if (done && draftKey) { clearDraft(draftKey); setSaved(null); } }, [done, draftKey]);
  useEffect(() => { if (dirty && saved && !restored.current) setSaved(null); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const request = (v) => { if (!v && saving) return; if (!v && dirty && !done) setConfirm(true); else onOpenChange(v); };
  const discard = () => { setConfirm(false); if (draftKey) clearDraft(draftKey); onOpenChange(false); };
  const keep = () => { setConfirm(false); onOpenChange(false); };
  const resume = () => { restored.current = true; onRestoreDraft?.(saved.value); setSaved(null); };

  return (
    <>
    <AlertDialog open={confirm} onOpenChange={setConfirm}>
      <AlertDialogContent className="max-w-sm rounded-3xl" data-testid="discard-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{draftKey ? "Save this as a draft?" : "Discard this record?"}</AlertDialogTitle>
          <AlertDialogDescription>{draftKey ? "You can pick up right where you left off next time." : discardText}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          {draftKey ? (
            <>
              <AlertDialogCancel onClick={discard} className="rounded-full border-0 shadow-none mt-0 bg-transparent text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="discard-confirm-btn">Discard</AlertDialogCancel>
              <AlertDialogCancel className="rounded-full border-0 bg-muted hover:bg-muted/70 focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="discard-keep-btn">Keep editing</AlertDialogCancel>
              <AlertDialogAction onClick={keep} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="draft-save-btn">Save draft</AlertDialogAction>
            </>
          ) : (
            <>
              <AlertDialogCancel className="rounded-full border-0 bg-muted hover:bg-muted/70 focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="discard-keep-btn">Keep editing</AlertDialogCancel>
              <AlertDialogAction className="rounded-full bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90" data-testid="discard-confirm-btn" onClick={discard}>Discard</AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Dialog open={open} onOpenChange={request}>
      <DialogContent data-testid={testid} className={`${maxW} ${done?.art ? SHEET.replace("max-sm:h-[94dvh]", "max-sm:h-auto max-sm:max-h-[94dvh]") : SHEET}`} hideClose guard={false}>
        <div className={`sm:hidden mx-auto mt-2.5 h-1 w-10 rounded-full ${done?.art ? "absolute left-1/2 -translate-x-1/2 top-0 z-10 bg-white/70" : "bg-muted-foreground/25"}`} aria-hidden />
        {done ? <DoneScreen done={done} color={doneColor} onClose={() => onOpenChange(false)} /> : (
        <>
        <div className="px-5 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0"><Progress steps={steps} step={step} /></div>
            <button type="button" onClick={() => request(false)} aria-label="Close" data-testid="dialog-close-btn"
              className="h-8 w-8 coarse:h-10 coarse:w-10 shrink-0 -mr-1 rounded-full flex items-center justify-center bg-muted/70 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
          <AnimatePresence>
            {saved && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden" data-testid="draft-banner">
                <div className="mt-4 rounded-2xl bg-primary/[0.08] pl-3.5 pr-1.5 py-1.5 flex items-center gap-2.5">
                  <ClockCounterClockwise weight="bold" className="h-4 w-4 text-primary shrink-0" />
                  <span className="flex-1 text-sm">Unfinished draft from {relativeTime(new Date(saved.at).toISOString())}</span>
                  <button type="button" onClick={() => { clearDraft(draftKey); setSaved(null); }} data-testid="draft-dismiss" className="h-8 px-3 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground">Start fresh</button>
                  <button type="button" onClick={resume} data-testid="draft-resume" className="h-8 px-3.5 rounded-full text-xs font-semibold bg-foreground text-background">Continue</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <p className="mt-5 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground min-w-0" data-testid="wizard-step-kicker">
            <span className="truncate">{kicker}</span><span className="shrink-0">· {step + 1} of {steps.length}</span>
          </p>
          <DialogTitle className="mt-1 text-2xl max-sm:text-xl font-semibold tracking-tight" data-testid="wizard-step-title">{s.title}</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-muted-foreground [@media(max-height:700px)]:max-sm:text-[13px]">{s.subtitle}</DialogDescription>
          {s.hint && (
            <motion.p key={s.key} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-3 flex items-start gap-2 text-xs text-muted-foreground/90 leading-relaxed [@media(max-height:700px)]:max-sm:hidden" data-testid="wizard-step-hint">
              <Lightbulb weight="fill" className="h-3.5 w-3.5 mt-px shrink-0 text-[#E8A100]" />{s.hint}
            </motion.p>
          )}
        </div>
        <div key={`body-${s.key}`} className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-8 pt-1.5 pb-6">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={s.key} onAnimationComplete={(d) => d?.opacity === 1 && step > 0 && focusFirstIn(document.querySelector(`[data-testid="${testid}"]`), true)} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2, ease: "easeOut" }}>
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
        {!hideFooter && (
          <div className="px-4 sm:px-8 py-4 flex items-center gap-1 sm:gap-2 bg-background/90 dark:bg-card/95 backdrop-blur shadow-[0_-1px_0_hsl(var(--border)/0.6)] max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]">
            {step > 0 ? (
              <button type="button" onClick={onBack} data-testid="wizard-back-btn" className="h-11 px-3 sm:px-4 shrink-0 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted inline-flex items-center gap-1.5 transition-colors">
                <ArrowLeft className="h-4 w-4" />Back
              </button>
            ) : <button type="button" onClick={() => request(false)} data-testid="wizard-cancel-btn" className="h-11 px-4 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Cancel</button>}
            <div className="flex-1" />
            {s.optional && onSkip && !isLast && (
              <button type="button" onClick={onSkip} data-testid="wizard-skip-btn" className="h-11 px-3 sm:px-4 shrink-0 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Skip</button>
            )}
            {isLast ? (
              <button type="button" onClick={onFinish} disabled={saving || !canNext} data-testid="wizard-finish-btn" style={finishStyle}
                className="h-11 px-5 sm:px-6 min-w-0 whitespace-nowrap rounded-full text-sm font-semibold bg-foreground text-background inline-flex items-center gap-1.5 disabled:opacity-50 active:scale-[0.97] transition-[transform,opacity]">
                {saving ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Check weight="bold" className="h-4 w-4" />}{finishLabel}
              </button>
            ) : (
              <button type="button" onClick={onNext} disabled={!canNext} data-testid="wizard-next-btn"
                className="h-11 px-5 sm:px-6 shrink-0 whitespace-nowrap rounded-full text-sm font-semibold bg-foreground text-background inline-flex items-center gap-1.5 disabled:opacity-40 active:scale-[0.97] transition-[transform,opacity]">
                Next<ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
